import React, { useEffect, useRef } from 'react';
import { BlockNoteSchema, defaultBlockSpecs, defaultStyleSpecs } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { useCreateBlockNote } from '@blocknote/react';
import { useSubjectLanguageVariants } from '@features/user';
import { isEmptyNoteContent, parseNoteToBlocks } from '@utils/blocknote';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';

import { PlantIcon } from '../../../icons/PlantIcon';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

// Same schema as the editor — no media or code blocks
const {
  codeBlock: _codeBlock,
  audio: _audio,
  video: _video,
  image: _image,
  file: _file,
  ...blockSpecs
} = defaultBlockSpecs;
const { code: _code, ...styleSpecs } = defaultStyleSpecs;
const viewerSchema = BlockNoteSchema.create({ blockSpecs, styleSpecs });

export interface TreatmentPlanData {
  markdown: string;
  treatment_plan_id: string | null;
}

interface PatientTreatmentPlanCardProps {
  data: TreatmentPlanData | null;
  isLoading?: boolean;
  onEdit?: () => void;
}

export const PatientTreatmentPlanCard: React.FC<PatientTreatmentPlanCardProps> = ({
  data,
  isLoading = false,
  onEdit,
}) => {
  const { singular } = useSubjectLanguageVariants();
  const hasContent = data && !isEmptyNoteContent(data.markdown);
  const editor = useCreateBlockNote({ schema: viewerSchema });
  const loadedRef = useRef<string | null>(null);

  // Load content into the read-only editor when data changes
  useEffect(() => {
    if (!hasContent || !data) return;
    if (loadedRef.current === data.markdown) return;
    loadedRef.current = data.markdown;

    void (async () => {
      const blocks = await parseNoteToBlocks(editor, data.markdown);
      if (blocks.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        editor.replaceBlocks(editor.document, blocks);
      }
    })();
  }, [editor, data, hasContent]);

  return (
    <Card
      sx={{
        width: '100%',
        minWidth: 400,
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 3,
          borderColor: 'borderColors.brand',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <PlantIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              color: 'textColors.darkText',
              mb: 0.25,
            }}
          >
            Treatment Plan
          </Typography>
        </Box>
        {onEdit && (
          <Tooltip title="Edit Treatment Plan">
            <IconButton size="small" onClick={onEdit} sx={{ ml: 'auto' }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent
        onClick={hasContent && onEdit ? onEdit : undefined}
        sx={{
          p: 1.5,
          pt: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          maxHeight: 400,
          ...(hasContent && onEdit ? { cursor: 'pointer' } : {}),
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Loading treatment plan...
            </Typography>
          </Box>
        ) : !hasContent ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: onEdit ? 1.5 : 0 }}>
              {`No treatment plan for this ${singular}.`}
            </Typography>
            {onEdit && (
              <Button
                variant="outlined"
                size="small"
                onClick={onEdit}
                sx={{ fontSize: '0.75rem' }}
              >
                Edit
              </Button>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: 'backgroundColors.tertiary',
              border: '1px solid',
              borderColor: 'borderColors.lightprimary',
            }}
          >
            <BlockNoteView
              editor={editor}
              editable={false}
              theme="light"
              formattingToolbar={false}
              sideMenu={false}
              slashMenu={false}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
